import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import PropType from "prop-types";
import { bindActionCreators } from "redux";

import {
  Popover, Button, IconButton, Typography, Grid, Container, List,
  Divider, ListItem, ListItemText, ListItemSecondaryAction,
  TextField, CircularProgress, InputAdornment,
} from "@material-ui/core";
import {
  Add, Edit, AccountBalanceWallet, AttachMoney
} from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";

import * as PaymentActions from "../actions/PaymentActions";
import * as PaymentPlanActions from "../actions/PaymentPlanActions";
import { addNextInstallment, formatPrice, getNumberWithOrdinal, onReturnKeyPress } from '../utils';

const styles = (theme) => ({
  paymentPlanHeader: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridGap: "6px",
    marginTop: "10px",
  },
  listItemRoot: {
    width: "85%",
    paddingRight: "0",
  },
  listItemAvatarRoot: {
    marginRight: "16px",
    minWidth: "unset",
  },
  payPalPayBtn: {
    margin: "0 0 10px 0",
    height: "3rem"
  },
});

// Cart Icon with Price
const PaymentPlanIcon = withStyles({
  label: {
    display: "grid",
    gridTemplateColumns: "max-content",
    gridGap: "6px",
    color: "white",
  },
})(IconButton);

class PaymentPlans extends Component {
  constructor(props) {
    super(props);

    this.state = {
      open: false,
      popoverAnchorEl: null,
      listLoading: false,
      editNextPayment: false,
      nextPaymentValue: ''
    }

    this.nextPaymentTextInput = null;

    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.renderList = this.renderList.bind(this);
    this.toggleEditMode = this.toggleEditMode.bind(this);
    this.onPaymentPlanChange = this.onPaymentPlanChange.bind(this);
    this.onAddInstallment = this.onAddInstallment.bind(this);
    this.validatePaymentPlanValue = this.validatePaymentPlanValue.bind(this);
  }

  handleOpen(e) {
    this.setState({ open: true, popoverAnchorEl: e.currentTarget });
  }
  
  handleClose() {
    this.setState({ open: false, popoverAnchorEl: null });
  }

  toggleEditMode() {
    this.setState({ editNextPayment: !this.state.editNextPayment });
  }

  onPaymentPlanChange(nextPaymentValue) {
    this.setState({ nextPaymentValue });
  }

  validatePaymentPlanValue() {
    const { state, props } = this;
    const { nextPaymentValue } = state;
    const { coupon, actions, products, installments } = props;
    let totalPrice = products.reduce((total, { price }) => total += price, 0);
    const totalPaidInstallments = installments.reduce((total, { amount, paid }) => paid ? total += amount : total, 0);
    let paymentPlanError = '';

    if (!coupon || !coupon.plan || !coupon.plan._id) {
      paymentPlanError = 'Something went wrong';
      actions.openSnackbar('error', 'Something went wrong');
    }

    const { minAmount } = coupon.plan;
    const { discount } = coupon;

    if (discount && discount > 0) {
      totalPrice -= (totalPrice * discount) / 100;
    }

    totalPrice -= totalPaidInstallments;
    totalPrice = Number(totalPrice.toFixed(2));

    if (isNaN(Number(nextPaymentValue))) {
      paymentPlanError = 'Invalid Payment amount';
      actions.openSnackbar('error', 'Invalid Payment amount');
    }

    if (Number(nextPaymentValue) < minAmount) {
      if ((totalPrice - Number(nextPaymentValue)) > 0) {
        paymentPlanError = `Payment value must not be less than $${formatPrice(minAmount)}`;
        actions.openSnackbar('error', `Payment value must not be less than $${formatPrice(minAmount)}`);
      }
    }
    
    if (Number(nextPaymentValue) > totalPrice) {
      paymentPlanError = `Payment value must not be more than $${formatPrice(totalPrice)}`;
      actions.openSnackbar('error', `Payment value must not be more than $${formatPrice(totalPrice)}`);
    }

    if (paymentPlanError.length > 0) {
      this.setState({ nextPaymentValue: `${minAmount}`, paymentPlanError });
      return false;
    }

    return true;
  }

  onAddInstallment = async () => {
    const { actions, paymentPlanActions, orderId } = this.props;
    const { nextPaymentValue } = this.state;
    if(!this.validatePaymentPlanValue()) return;

    this.setState({ listLoading: true });

    try {

      const { order } = await addNextInstallment(orderId, Number(nextPaymentValue));

      if (order.installments) {
        paymentPlanActions.setInstallments(order.installments);
        actions.openSnackbar('success', 'Payment Updated');
        this.setState({ editNextPayment: false })
      } else throw new Error('error', 'Something went wrong');
      
    } catch (error) {
      console.error(error);
      actions.openSnackbar('error', error.message || 'Something went wrong');
    } finally {
      this.setState({ listLoading: false });
    }
  }

  renderList() {
    const { props } = this;
    const { classes, products } = props;

    const items = products || [];

    if (items.length <= 0) return (
      <ListItem>
        <ListItemText primary="No Payment Plans in progress" primaryTypographyProps={{ variant: "h6", noWrap: true, style: { textAlign: "center" } }} />
      </ListItem>
    );
    else return items.map(({ name, price }, i) => {
      return (
        <div key={`item-${i}`}>
          <Divider orientation="horizontal"/>
          <ListItem classes={{ root: classes.listItemRoot }}>
            <ListItemText
              primary={name}
              primaryTypographyProps={{ variant: "body1", noWrap: true }}
            />
            <ListItemSecondaryAction style={{ top: "34%" }}>
              <Typography variant="body2">${formatPrice(price)}</Typography>
            </ListItemSecondaryAction>
          </ListItem>
        </div>
        )
      }
    );
  }

  renderDiscountListItem() {
    const { props } = this;
    const { coupon, classes, products, createdAt } = props;
    const content = [];

    if (!coupon || coupon.discount === undefined || coupon.discount === null) content.push(null);
    else {
      const hasPlan = coupon.plan && coupon.plan._id;
      const totalPrice = products.reduce((total, { price }) => total += price, 0);
      const discountedPrice = coupon.discount > 0 ? (totalPrice * coupon.discount)  / 100 : 0;

      let paymentPlanDeadline = null;
      if (new Date(createdAt) instanceof Date) {
        paymentPlanDeadline = new Date(createdAt).setDate(new Date(createdAt).getDate() + coupon.plan.lifeSpan);
      }

      content.push(<Divider key="discount-1" orientation="horizontal" />);

      content.push(
        <ListItem key="discount-2" classes={{ root: classes.listItemRoot }}>
          <ListItemText
            primary="Applied Coupon"
            secondary={<div style={{ display: 'grid' }}>
              <span>{`${coupon.discount}% discount ${hasPlan ? '+ Payment Plan option' : ''}`}</span>
              <span><strong>Minimum Payment:</strong> ${formatPrice(coupon.plan.minAmount)}</span>
              <span><strong>Deadline:</strong> {paymentPlanDeadline? new Date(paymentPlanDeadline).toLocaleDateString() : 'Date Missing'}</span>
            </div>}
            primaryTypographyProps={{ variant: "body1", noWrap: true }}
            secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
          />
          <ListItemSecondaryAction style={{ top: "34%" }}>
            <Typography variant="body2">-${formatPrice(discountedPrice)}</Typography>
          </ListItemSecondaryAction>
        </ListItem>
      );
    }

    return content;
  }
  
  renderInstallments() {
    const { props, state, toggleEditMode, onPaymentPlanChange, onAddInstallment } = this;
    const { editNextPayment, nextPaymentValue, listLoading } = state;
    const { coupon, classes, installments } = props;
    const content = [];
    const paidInstallments = installments.filter(({ paid }) => paid);
    const unPaidInstallments = installments.filter(({ paid }) => !paid);

    if (!coupon || coupon.discount === undefined || coupon.discount === null) content.push(null);
    else {
      const hasPlan = coupon.plan && coupon.plan._id;
      content.push(<Divider key="discount-3" orientation="horizontal" />);
  
      if (hasPlan) {
        paidInstallments.forEach((installment, index) => {
          content.push(<Fragment key={`paid-${index}`}>
            <ListItem classes={{ root: classes.listItemRoot }}>
              <ListItemText
                primary={`${getNumberWithOrdinal(index + 1)} installment`}
                secondary={`Paid on ${new Date(installment.paymentDate).toLocaleDateString()}`}
                primaryTypographyProps={{ variant: "body1", noWrap: true }}
                secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
              />
              <ListItemSecondaryAction style={{ top: "34%" }}>
                <Typography variant="body2">-${formatPrice(installment.amount)}</Typography>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider key="discount-3" orientation="horizontal" />
          </Fragment>);
        });
        
        if (unPaidInstallments.length <= 0 || editNextPayment) {
          content.push(<ListItem key={`edit-mode`}>
            <TextField
              placeholder="How much would you like to pay?"
              fullWidth
              value={nextPaymentValue}
              label="Next Payment"
              type="number"
              focused={true}
              autoComplete="off"
              inputRef={this.nextPaymentTextInput}
              onChange={(e) => onPaymentPlanChange(e.target.value)}
              onKeyDown={(e) => onReturnKeyPress(e, onAddInstallment)}
              InputProps={{
                startAdornment:
                  <InputAdornment position="start">
                    <AttachMoney style={{ color: "#2d1d1d8c", cursor: "pointer" }} fontSize="small"  color="disabled" />
                  </InputAdornment>,
                endAdornment:
                  <InputAdornment position="start">
                    <IconButton onClick={onAddInstallment}>
                    {listLoading ? <CircularProgress size={20} /> : <Add />}
                    </IconButton>
                  </InputAdornment>,
              }}
            />
          </ListItem>);
        } else {
          unPaidInstallments.forEach((installment, index) => {
            content.push(<Fragment key={`un-paid-${index}`}>
              <ListItem classes={{ root: classes.listItemRoot }}>
                <ListItemText
                  primary={`Next installment`}
                  secondary={`Amount $${formatPrice(installment.amount)}`}
                  primaryTypographyProps={{ variant: "body1", noWrap: true }}
                  secondaryTypographyProps={{ variant: "subtitle2", noWrap: true }}
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={toggleEditMode} disabled={listLoading}><Edit /></IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider key="discount-3" orientation="horizontal" />
            </Fragment>);
          })
        }
      }
    }


    return content;
  }

  renderOwingListItem = () => {
    const { props } = this;
    const { installments, owing } = props;
    const unpaidInstallment = installments.find(({ paid }) => !paid);
    const content = [];

    if (unpaidInstallment) {
      content.push(
        <Typography key='1' variant="h6" noWrap>
          <strong>{`$${formatPrice(unpaidInstallment.amount)} (Remaining $${formatPrice(owing - unpaidInstallment.amount)})`}</strong>
        </Typography>
      );
    } else {
      content.push(
        <Typography key='1' variant="h6" noWrap>
          <strong>$0 (Remaining ${formatPrice(owing)})</strong>
        </Typography>
      );
    }

    return (<>
      <ListItem>
        <ListItemText primary={<Typography variant="h6" noWrap>Total:</Typography>} />

        <ListItemSecondaryAction style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gridGap: '5px' }} >
          {content}
        </ListItemSecondaryAction>
      </ListItem>
    </>);
  }
  
  renderPaymentButtonListItem = () => {
    const { state, props } = this;
    const { listLoading } = state;
    const { actions, step, installments, classes } = props;
    const unpaidInstallment = installments.find(({ paid }) => !paid);
    const paymentValStr = unpaidInstallment ? `$${formatPrice(unpaidInstallment.amount)}` : '$0';
    let button = (
      <Button className={classes.payPalPayBtn} variant="contained" color="primary" fullWidth onClick={() => actions.setStep(4)} disabled={listLoading || !unpaidInstallment}>
        {listLoading ? <Typography variant="h5"> <CircularProgress size={20} />&nbsp;Please Wait...</Typography> : <Typography variant="h5">Pay {paymentValStr}</Typography>}
      </Button>
    );

    if (step === 4) button = (
      <Button className={classes.payPalPayBtn} variant="contained" color="primary" fullWidth onClick={() => actions.setStep(3)} >
        {listLoading ? <Typography variant="h5"> <CircularProgress size={20} />&nbsp;Please Wait...</Typography> : <Typography variant="h5">Back to Cart</Typography>}
      </Button>
    );

    return (<>
      <ListItem>
        <ListItemText primary={button} />
      </ListItem>
    </>);
  }

  render() {
    const { state, props, handleOpen, handleClose } = this;
    const { open, popoverAnchorEl } = state;
    const { classes, pageView } = props;

    if (pageView) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ paddingTop: "0" }}>
            <List dense={true}>
              {this.renderList()}
              {this.renderDiscountListItem()}
              {this.renderInstallments()}
              {this.renderOwingListItem()}

            </List>
          </Grid>
        </Grid>
      )
    }

    return (
      <div>
        <PaymentPlanIcon onClick={handleOpen}>
          <AccountBalanceWallet />
        </PaymentPlanIcon>
        <Popover
          open={open}
          anchorEl={popoverAnchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ paddingBottom: "0" }}>
              <Container className={classes.paymentPlanHeader}>
                <Typography variant="h5" noWrap style={{ fontWeight: "500", lineHeight: "2" }}>Payment Plan</Typography>
              </Container>
            </Grid>

            <Grid item xs={12} style={{ paddingTop: "0" }}>
              <List dense={true}>
                {this.renderList()}
                {this.renderDiscountListItem()}
                {this.renderInstallments()}
                {this.renderOwingListItem()}
                {this.renderPaymentButtonListItem()}

              </List>
            </Grid>
          </Grid>
        </Popover>
      </div>
    );
  }
}

PaymentPlans.propTypes = {
  classes: PropType.object.isRequired,
};

const mapStateToProps = ({ payment, paymentPlanReducer }) => ({
  step: payment.step,
  orderId: paymentPlanReducer.orderId,
  createdAt: paymentPlanReducer.createdAt,
  products: paymentPlanReducer.products,
  coupon: paymentPlanReducer.coupon,
  installments: paymentPlanReducer.installments,
  totalPrice: paymentPlanReducer.totalPrice,
  owing: paymentPlanReducer.owing,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(PaymentActions, dispatch),
  paymentPlanActions: bindActionCreators(PaymentPlanActions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(PaymentPlans));